import { __assign } from "tslib";
import { useCallback, useMemo, useState } from 'react';
import { KeyCode } from '../../internal/keycode';
import { findNavigableSeries, focusSVGElement, nextValidDomainIndex } from '../utils';
export function useNavigation(_a) {
    var series = _a.series, visibleSeries = _a.visibleSeries, scaledSeries = _a.scaledSeries, barGroups = _a.barGroups, barGroupsRef = _a.barGroupsRef, xScale = _a.xScale, yScale = _a.yScale, highlightedPoint = _a.highlightedPoint, highlightedGroupIndex = _a.highlightedGroupIndex, highlightedSeries = _a.highlightedSeries, isPopoverPinned = _a.isPopoverPinned, pinPopover = _a.pinPopover, highlightSeries = _a.highlightSeries, highlightGroup = _a.highlightGroup, highlightPoint = _a.highlightPoint, highlightPointByIndex = _a.highlightPointByIndex, clearHighlightedSeries = _a.clearHighlightedSeries;
    var _b = useState(null), targetX = _b[0], setTargetX = _b[1];
    // There are two different types of navigation:
    // 1) Group navigation for any chart that contains a bar series
    // 2) Line navigation for any chart that only contains lines and thresholds
    var isGroupNavigation = visibleSeries.some(function (_a) {
        var series = _a.series;
        return series.type === 'bar';
    });
    // Make a list of series that can be navigated between. Bar series are treated as one.
    var navigableSeries = useMemo(function () { return findNavigableSeries(visibleSeries); }, [visibleSeries]).navigableSeries;
    var focusGroup = useCallback(function (groupIndex) {
        if (barGroupsRef.current) {
            var barGroup = barGroupsRef.current.querySelector(":nth-child(" + (groupIndex + 1) + ")");
            if (barGroup) {
                focusSVGElement(barGroup);
            }
        }
    }, [barGroupsRef]);
    var onBarGroupFocus = function (e) {
        // IE11 does not support `dataset` on SVG elements
        var groupIndex = e.target.getAttribute('data-group');
        if (groupIndex !== null) {
            var group = parseInt(groupIndex);
            var xDomain = xScale.domain;
            setTargetX(xDomain[group]);
            highlightGroup(group);
            focusGroup(group);
        }
    };
    var onBarGroupClick = function (e) {
        e.preventDefault();
        pinPopover();
    };
    var onBarGroupMouseOver = function (e) {
        if (isPopoverPinned) {
            return;
        }
        // IE11 does not support `dataset` on SVG elements
        var groupIndex = e.target.getAttribute('data-group');
        if (groupIndex !== null) {
            highlightGroup(parseInt(groupIndex));
        }
    };
    var onBarGroupMouseOut = useCallback(function () {
        if (isPopoverPinned) {
            return;
        }
        clearHighlightedSeries();
    }, [isPopoverPinned, clearHighlightedSeries]);
    var moveBetweenSeries = useCallback(function (direction) {
        var _a, _b;
        if (isGroupNavigation) {
            return;
        }
        var xOffset = xScale.isCategorical() ? Math.max(0, xScale.d3Scale.bandwidth() - 1) / 2 : 0;
        var MAX_SERIES_INDEX = navigableSeries.length - 1;
        // Find the index of the currently highlighted series (if any)
        var previousSeriesIndex = -1;
        if (highlightedSeries) {
            previousSeriesIndex = navigableSeries.indexOf(highlightedSeries);
        }
        // Move forwards or backwards to the new series
        var nextSeriesIndex = 0;
        if (previousSeriesIndex !== -1) {
            nextSeriesIndex = previousSeriesIndex + direction;
            if (nextSeriesIndex > MAX_SERIES_INDEX) {
                nextSeriesIndex = 0;
            }
            else if (nextSeriesIndex < 0) {
                nextSeriesIndex = MAX_SERIES_INDEX;
            }
        }
        var nextSeries = navigableSeries[nextSeriesIndex];
        var nextInternalSeries = series.filter(function (_a) {
            var series = _a.series;
            return series === nextSeries;
        })[0];
        // 2. Find point in the next series
        var targetXPoint = ((_a = xScale.d3Scale(targetX)) !== null && _a !== void 0 ? _a : NaN) + xOffset;
        if (!isFinite(targetXPoint)) {
            targetXPoint = 0;
        }
        if (nextSeries.type === 'line') {
            var nextSeriesData = nextSeries.data;
            var lookingForScaled_1 = targetXPoint; // scaled X in previous series
            var nextPoint = nextSeriesData
                // scale all points in series
                .map(function (d) { return ({
                x: (xScale.d3Scale(d.x) || 0) + xOffset,
                y: yScale.d3Scale(d.y) || 0,
                datum: d
            }); })
                // find the closest point to previous X
                .reduce(function (prev, curr) { return (Math.abs(curr.x - lookingForScaled_1) < Math.abs(prev.x - lookingForScaled_1) ? curr : prev); }, { x: -Infinity, y: -Infinity });
            highlightSeries(nextSeries);
            highlightPoint(__assign(__assign({}, nextPoint), { color: nextInternalSeries.color, series: nextSeries }));
        }
        else if (nextSeries.type === 'threshold') {
            highlightSeries(nextSeries);
            highlightPoint({
                x: targetXPoint,
                y: (_b = yScale.d3Scale(nextSeries.y)) !== null && _b !== void 0 ? _b : NaN,
                color: nextInternalSeries.color,
                series: nextSeries
            });
        }
    }, [
        isGroupNavigation,
        xScale,
        navigableSeries,
        highlightedSeries,
        series,
        targetX,
        highlightSeries,
        highlightPoint,
        yScale,
    ]);
    var moveWithinSeries = useCallback(function (direction) {
        var series = highlightedSeries || visibleSeries[0].series;
        var previousPoint = highlightedPoint || scaledSeries[0];
        if (series.type === 'line') {
            // find previous point in series
            var indexOfPreviousPoint = (previousPoint === null || previousPoint === void 0 ? void 0 : previousPoint.datum)
                ? series.data.indexOf(previousPoint.datum)
                : 0;
            var MAX_POINT_INDEX = series.data.length - 1;
            // find next point
            var nextPointIndex = indexOfPreviousPoint + direction;
            if (nextPointIndex > MAX_POINT_INDEX) {
                nextPointIndex = 0;
            }
            else if (nextPointIndex < 0) {
                nextPointIndex = MAX_POINT_INDEX;
            }
            var nextPoint_1 = series.data[nextPointIndex];
            // find scaled next point
            var nextPointScaled = scaledSeries.filter(function (s) { return s.datum === nextPoint_1; })[0] || null;
            setTargetX(nextPoint_1.x);
            highlightSeries(series);
            highlightPoint(nextPointScaled);
        }
        else if (series.type === 'bar') {
            var xDomain = xScale.domain;
            var MAX_GROUP_INDEX = xDomain.length - 1;
            var nextGroupIndex = 0;
            if (highlightedGroupIndex !== null) {
                // find next group
                nextGroupIndex = highlightedGroupIndex + direction;
                if (nextGroupIndex > MAX_GROUP_INDEX) {
                    nextGroupIndex = 0;
                }
                else if (nextGroupIndex < 0) {
                    nextGroupIndex = MAX_GROUP_INDEX;
                }
            }
            var nextDomainIndex = nextValidDomainIndex(nextGroupIndex, barGroups, direction);
            setTargetX(xDomain[nextDomainIndex]);
            highlightGroup(nextDomainIndex);
            focusGroup(nextDomainIndex);
        }
    }, [
        highlightedSeries,
        visibleSeries,
        highlightedPoint,
        scaledSeries,
        highlightSeries,
        highlightPoint,
        xScale.domain,
        highlightedGroupIndex,
        barGroups,
        highlightGroup,
        focusGroup,
    ]);
    var onKeyDown = useCallback(function (event) {
        var keyCode = event.keyCode;
        if (keyCode !== KeyCode.up &&
            keyCode !== KeyCode.right &&
            keyCode !== KeyCode.down &&
            keyCode !== KeyCode.left &&
            keyCode !== KeyCode.space &&
            keyCode !== KeyCode.enter) {
            return;
        }
        event.preventDefault();
        if (isPopoverPinned) {
            return;
        }
        if (keyCode === KeyCode.down || keyCode === KeyCode.up) {
            moveBetweenSeries(keyCode === KeyCode.down ? 1 : -1);
        }
        else if (keyCode === KeyCode.left || keyCode === KeyCode.right) {
            moveWithinSeries(keyCode === KeyCode.right ? 1 : -1);
        }
        else if (keyCode === KeyCode.enter || keyCode === KeyCode.space) {
            pinPopover();
        }
    }, [moveWithinSeries, moveBetweenSeries, isPopoverPinned, pinPopover]);
    return {
        isGroupNavigation: isGroupNavigation,
        highlightedSeries: highlightedSeries,
        highlightedPoint: highlightedPoint,
        highlightedGroupIndex: highlightedGroupIndex,
        highlightPoint: highlightPoint,
        clearHighlightedSeries: clearHighlightedSeries,
        barGroupProps: {
            onMouseOver: onBarGroupMouseOver,
            onMouseOut: onBarGroupMouseOut,
            onFocus: onBarGroupFocus,
            onClick: onBarGroupClick,
            onKeyDown: onKeyDown
        },
        lineSeriesProps: {
            onKeyDown: onKeyDown,
            onFocus: function (series) {
                highlightSeries(series);
                if (!highlightedPoint) {
                    highlightPointByIndex(series, 0);
                }
            }
        }
    };
}
//# sourceMappingURL=use-navigation.js.map